import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["border", "children", "color", "level", "margin", "elementRef", "makeStyles", "variant"];
var _dec, _class, _Heading, _IconAiColoredSolid, _IconAiColoredSolid2, _IconAiColoredSolid3, _IconAiColoredSolid4, _IconAiColoredSolid5, _IconAiColoredSolid6;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { View } from '@instructure/ui-view';
import { passthroughProps, callRenderProp } from '@instructure/ui-react-utils';
import { IconAiColoredSolid } from '@instructure/ui-icons';
import { withStyle } from '@instructure/emotion';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
const variantLevels = {
  titlePageDesktop: 'h1',
  titlePageMobile: 'h1',
  titleSection: 'h2',
  titleCardSection: 'h2',
  titleModule: 'h3',
  titleCardLarge: 'h4',
  titleCardRegular: 'h4',
  titleCardMini: 'h4',
  label: 'h5',
  labelInline: 'h5'
};

/**
---
category: components
---
**/
let Heading = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Heading = class Heading extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  checkProps() {
    const _this$props = this.props,
      variant = _this$props.variant,
      as = _this$props.as;
    if (variant) {
      if (as) {}
    }
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
    this.checkProps();
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props3;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
    this.checkProps();
  }
  renderContent() {
    const _this$props4 = this.props,
      children = _this$props4.children,
      renderIcon = _this$props4.renderIcon,
      aiVariant = _this$props4.aiVariant;
    if (renderIcon && !aiVariant) {
      var _this$props$styles;
      return _jsxs("span", {
        css: [(_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.withIcon],
        "aria-hidden": "true",
        children: [callRenderProp(renderIcon), "\xA0", children]
      });
    }
    if (aiVariant === 'stacked') {
      var _this$props$styles2, _this$props$styles3, _this$props$styles4;
      return _jsxs("span", {
        css: [(_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.withIcon],
        "aria-hidden": "true",
        children: [_jsxs("span", {
          css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.igniteAIStacked,
          children: [_IconAiColoredSolid || (_IconAiColoredSolid = _jsx(IconAiColoredSolid, {})), _jsx("span", {
            css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.igniteAI,
            children: "IgniteAI"
          })]
        }), children]
      });
    }
    if (aiVariant === 'horizontal') {
      var _this$props$styles5, _this$props$styles6;
      return _jsxs("span", {
        css: (_this$props$styles5 = this.props.styles) === null || _this$props$styles5 === void 0 ? void 0 : _this$props$styles5.withIcon,
        "aria-hidden": "true",
        children: [_IconAiColoredSolid2 || (_IconAiColoredSolid2 = _jsx(IconAiColoredSolid, {})), _jsx("span", {
          css: (_this$props$styles6 = this.props.styles) === null || _this$props$styles6 === void 0 ? void 0 : _this$props$styles6.igniteAI,
          children: "IgniteAI"
        }), children]
      });
    }
    if (aiVariant === 'iconOnly') {
      var _this$props$styles7;
      return _jsxs("span", {
        css: (_this$props$styles7 = this.props.styles) === null || _this$props$styles7 === void 0 ? void 0 : _this$props$styles7.withIcon,
        "aria-hidden": "true",
        children: [_IconAiColoredSolid3 || (_IconAiColoredSolid3 = _jsx(IconAiColoredSolid, {})), "\xA0", children]
      });
    }
    if (aiVariant === 'stacked') {
      var _this$props$styles8, _this$props$styles9;
      return _jsxs(_Fragment, {
        children: [_jsxs("span", {
          css: (_this$props$styles8 = this.props.styles) === null || _this$props$styles8 === void 0 ? void 0 : _this$props$styles8.igniteAIStacked,
          children: [_IconAiColoredSolid4 || (_IconAiColoredSolid4 = _jsx(IconAiColoredSolid, {})), _jsx("span", {
            css: (_this$props$styles9 = this.props.styles) === null || _this$props$styles9 === void 0 ? void 0 : _this$props$styles9.igniteAI,
            children: "IgniteAI"
          })]
        }), children]
      });
    }
    if (aiVariant === 'horizontal') {
      var _this$props$styles0;
      return _jsxs(_Fragment, {
        children: [_IconAiColoredSolid5 || (_IconAiColoredSolid5 = _jsx(IconAiColoredSolid, {})), _jsx("span", {
          css: (_this$props$styles0 = this.props.styles) === null || _this$props$styles0 === void 0 ? void 0 : _this$props$styles0.igniteAI,
          children: "IgniteAI"
        }), children]
      });
    }
    if (aiVariant === 'iconOnly') {
      return _jsxs(_Fragment, {
        children: [_IconAiColoredSolid6 || (_IconAiColoredSolid6 = _jsx(IconAiColoredSolid, {})), "\xA0", children]
      });
    }
    return children;
  }

  //overriding default screen reader functionality is needed to read spans in h tags correctly
  getAriaLabel() {
    const _this$props5 = this.props,
      aiVariant = _this$props5.aiVariant,
      children = _this$props5.children,
      renderIcon = _this$props5.renderIcon;
    if (aiVariant === 'stacked' || aiVariant === 'horizontal') {
      return `IgniteAI, ${children}`;
    }
    if (aiVariant === 'iconOnly' || renderIcon) {
      return `${children}`;
    }
    return void 0;
  }
  render() {
    var _this$props$styles1;
    const _this$props6 = this.props,
      border = _this$props6.border,
      children = _this$props6.children,
      color = _this$props6.color,
      level = _this$props6.level,
      margin = _this$props6.margin,
      elementRef = _this$props6.elementRef,
      makeStyles = _this$props6.makeStyles,
      variant = _this$props6.variant,
      props = _objectWithoutProperties(_this$props6, _excluded);
    let ElementType = 'h2';
    if (variant) {
      // TODO deprecated, remove. `variant` should not set DOM level
      if (level) {
        if (level === 'reset') {
          ElementType = 'span';
        } else {
          ElementType = level;
        }
      } else {
        ElementType = variantLevels[variant];
      }
    } else if (props.as) {
      ElementType = props.as;
    } else if (level) {
      if (level === 'reset') {
        ElementType = 'span';
      } else {
        ElementType = level;
      }
    }
    return _jsx(View, {
      "aria-label": this.getAriaLabel(),
      ...passthroughProps(props),
      css: (_this$props$styles1 = this.props.styles) === null || _this$props$styles1 === void 0 ? void 0 : _this$props$styles1.heading,
      as: ElementType,
      elementRef: this.handleRef,
      margin: margin,
      "data-cid": "Heading",
      children: this.renderContent()
    });
  }
}, _Heading.displayName = "Heading", _Heading.componentId = 'Heading', _Heading.allowedProps = allowedProps, _Heading.defaultProps = {
  children: null,
  border: 'none',
  color: 'inherit'
}, _Heading)) || _class);
export default Heading;
export { Heading };